/**
 * Perícias: tudo o que o mundo junta em torno de cada perícia.
 *
 * <p><b>A página não guarda o mundo, ela o consulta.</b> Quem diz que um NPC ensina Forja
 * é a ficha do NPC; quem diz que a Prata é coletável de Mineração é a ficha do item. A
 * página lê essas marcas pelo backend (`PericiaService.java`) e as junta numa leitura só.
 * Escrever aqui abriria um segundo caminho para o mesmo vínculo, e dois caminhos
 * divergem — é a regra 1 do CLAUDE.md.
 *
 * <p>O que é dela é a <b>lore</b>: texto de Game Design, gravado no documento da página
 * como nas outras telas da seção.
 *
 * <p>O mapa lê o documento da <b>Tier List</b> — até que tier a perícia vai em cada área.
 * É o mesmo dado, não uma cópia: mudar lá muda aqui.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { TituloDaPagina } from '../../shell/TituloDaPagina';
import { pontosDoHexagono } from '../mundo/geometriaDoMapa';
import * as mundoApi from '../mundo/mundoApi';
import { PericiaIcone } from '../mundo/PericiaIcone';
import type { ItemDaPericia, Mapa, PaginaDaPericia, Resumo } from '../mundo/tipos';
import { semTier } from './nomeDaArea';
import {
  BLOCOS_DE_LORE,
  GRUPOS,
  fontesDoItem,
  interpretarDocumento,
  loreEmBranco,
  naturezaDe,
  numeroDoTier,
  ondeFica,
  parDe,
  porTier,
} from './paginaDePericia';
import type { DocumentoDePericias, LoreDaPericia, TipoDeFonte } from './paginaDePericia';
import { COR_INATIVA, corDe, periciaPorId } from './pericias';
import { AvisoDeGravacao, PAGINAS, useRascunho } from './rascunhoDeGameDesign';
import { interpretarRolagens, rolagensEmBranco } from './rolagens';
import { mapaDaSeason } from './tierDaArea';
import type { AreaComTier } from './tierDaArea';
import { interpretarTetos, REGIAO_DONA, TIER_DE_TODA_AREA } from './tierDaPericia';
import type { TetosPorArea } from './tierDaPericia';
import './areasETier.css';
import './pericias.css';

/** A mesma escala de cor da Tier List — o T4 tem de ser a mesma cor nas duas telas. */
const COR_TIER: Record<number, string> = {
  1: '#93b089',
  2: '#bfae6a',
  3: '#d29a4a',
  4: '#c26a3d',
  5: '#b4483f',
};

/** A cor de cada forma de conseguir um item. Coletar leva a cor da própria perícia. */
const COR_DA_FONTE: Record<Exclude<TipoDeFonte, 'coleta'>, string> = {
  criatura: '#c85b54',
  npc: '#e6c787',
  quest: '#7f9bb5',
};

const PRIMEIRA = 'mineracao';

/** Só o que a página lê do documento da Tier List — ela nunca escreve nele. */
const interpretarTierList = (guardado: unknown): { tetos: TetosPorArea } => ({
  tetos: interpretarTetos((guardado as { tetos?: unknown } | null)?.tetos),
});

const chaveDa = (a: AreaComTier) => `${a.regiaoSlot}-${a.slot}`;

export function Pericias() {
  const { pericia: daRota } = useParams();
  const navegar = useNavigate();
  const id = daRota && periciaPorId(daRota) ? daRota : PRIMEIRA;
  const pericia = periciaPorId(id)!;
  const natureza = naturezaDe(id);
  const futura = !!pericia.depoisDaSeason1;
  const abrir = (outra: string) => navegar(`/game-design/pericias/${outra}`);

  /** O item cujo "onde encontrar" o mapa está acendendo. */
  const [itemAceso, setItemAceso] = useState<number | null>(null);

  const doc = useRascunho<DocumentoDePericias>(PAGINAS.pericias, interpretarDocumento);
  const tierList = useRascunho(PAGINAS.tierList, interpretarTierList);
  const rolagens = useRascunho(PAGINAS.rolagens, interpretarRolagens);

  const { data: pagina, isLoading, error } = useQuery<PaginaDaPericia>({
    queryKey: ['mundo', 'pericia', id],
    queryFn: () => mundoApi.carregarPericia(id),
  });
  const { data: mapa } = useQuery<Mapa>({
    queryKey: ['mundo', 'mapa'],
    queryFn: mundoApi.carregarMapa,
    staleTime: Infinity,
  });
  const season = useMemo(() => (mapa ? mapaDaSeason(mapa.regioes) : null), [mapa]);

  const lore: LoreDaPericia = doc.valor?.lore[id] ?? loreEmBranco();
  const mudarLore = (chave: keyof LoreDaPericia, texto: string) =>
    doc.definir((atual) => ({
      ...atual,
      lore: { ...atual.lore, [id]: { ...(atual.lore[id] ?? loreEmBranco()), [chave]: texto } },
    }));

  const tetos = tierList.valor?.tetos ?? {};
  const tetoNa = (a: AreaComTier) => tetos[chaveDa(a)]?.[id];
  const contagem = [3, 4, 5].map((t) => ({
    t,
    n: season ? season.areas.filter((a) => tetoNa(a) === t).length : 0,
  }));

  const equacao = (rolagens.valor ?? rolagensEmBranco())[id];
  const rolagem =
    equacao && equacao.atributoA && equacao.atributoB
      ? `${equacao.atributoA} + ${equacao.atributoB} + ${equacao.dado}`
      : 'a definir na Ficha';

  const dona = REGIAO_DONA[id];
  const nomeDaDona = season?.regioes.find((r) => r.slot === dona)?.nome;
  const par = parDe(id);
  const itens = (pagina?.itens ?? []).slice().sort(porTier);
  const aceso = itens.find((i) => i.item.id === itemAceso);
  const temMateria = !futura && natureza !== 'combate';

  return (
    <div className="gd pe">
      <TituloDaPagina>
        Cada perícia com o que o mundo junta em torno dela: quem a ensina, que quests a
        pedem, onde se consegue o que ela usa — e a lore do caminho dela.
      </TituloDaPagina>

      <div className="pe-corpo">
        {/* --------------------------------------------------------- a lista */}
        <nav className="pe-lista" aria-label="Perícias">
          {GRUPOS.map((g) => (
            <div key={g.rotulo} className="pe-grupo">
              <span className="pe-grupo-rotulo">{g.rotulo}</span>
              {g.pericias.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`pe-item${p.id === id ? ' on' : ''}${p.depoisDaSeason1 ? ' futura' : ''}`}
                  style={{ '--c': corDe(p) } as React.CSSProperties}
                  onClick={() => {
                    setItemAceso(null);
                    abrir(p.id);
                  }}
                  aria-current={p.id === id ? 'page' : undefined}
                >
                  <PericiaIcone pericia={p.id} cor={corDe(p)} tamanho={24} />
                  <span>
                    <b>{p.rotulo}</b>
                    {p.depoisDaSeason1 && <small>Depois da Season 1</small>}
                  </span>
                </button>
              ))}
            </div>
          ))}
        </nav>

        <main className="pe-conteudo">
          {/* ------------------------------------------------------ cabeçalho */}
          <section className="pe-cabeca" style={{ '--c': corDe(pericia) } as React.CSSProperties}>
            <div className="pe-cabeca-ic">
              <PericiaIcone pericia={id} cor={corDe(pericia)} tamanho={54} />
            </div>
            <div className="pe-cabeca-txt">
              <div className="pe-cabeca-titulo">
                <h2>{pericia.rotulo}</h2>
                <span className="pe-natureza">
                  {natureza === 'coleta' ? 'Coleta' : natureza === 'criacao' ? 'Criação' : 'Combate'}
                </span>
              </div>
              <div className="pe-etiquetas">
                <span>
                  Rolagem: <b>{rolagem}</b>
                </span>
                {nomeDaDona && (
                  <span className="dona">
                    T4 e T5 só em <b>{nomeDaDona}</b>
                  </span>
                )}
                {par && (
                  <button type="button" onClick={() => abrir(par.id)}>
                    {natureza === 'coleta' ? 'Alimenta' : 'Matéria-prima de'}{' '}
                    <b style={{ color: corDe(par) }}>{par.rotulo} →</b>
                  </button>
                )}
              </div>
            </div>
            {temMateria && (
              <div className="pe-contagem" aria-label="Áreas por tier, da Tier List">
                {contagem.map(({ t, n }) => (
                  <Link
                    key={t}
                    to="/game-design/areas-e-tier"
                    style={{ '--c': COR_TIER[t] } as React.CSSProperties}
                    title="Escolhido na Tier List"
                  >
                    <b>{n}</b>
                    <span>áreas até T{t}</span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          {futura && (
            <p className="pe-futura">
              <b>Só depois da Season 1.</b> A página existe para a perícia não sumir do
              desenho; as fichas da Season 1 não podem marcá-la. A lore, essa sim, já pode
              ser escrita.
            </p>
          )}

          {doc.erroAoLer ? (
            <p className="gd-gravacao erro">
              {doc.erroAoLer instanceof Error ? doc.erroAoLer.message : 'Não foi possível abrir.'}
            </p>
          ) : (
            <AvisoDeGravacao estado={doc.estado} />
          )}

          {/* ---------------------------------------------------------- lore */}
          <section className="gd-secao" id="lore">
            <h2>Lore — o caminho da perícia</h2>
            <div className="pe-lore">
              {BLOCOS_DE_LORE.map((b) => (
                <label key={b.chave} className="pe-lore-bloco">
                  <span className="pe-lore-titulo">{b.titulo}</span>
                  <textarea
                    value={lore[b.chave]}
                    placeholder={b.dica}
                    rows={7}
                    onChange={(e) => mudarLore(b.chave, e.target.value)}
                  />
                </label>
              ))}
            </div>
          </section>

          {isLoading && <p className="gd-nota">Carregando o que o mundo tem desta perícia…</p>}
          {error && (
            <p className="gd-gravacao erro">
              {error instanceof Error ? error.message : 'Não consegui carregar a perícia.'}
            </p>
          )}

          {pagina && !futura && (
            <>
              {/* ------------------------------------------------------ NPCs */}
              <section className="gd-secao" id="npcs">
                <h2>NPCs</h2>
                <p className="gd-nota">
                  Quem ensina, compra, vende ou disputa o ofício. Entra aqui o NPC que marca
                  {' '}<b>{pericia.rotulo}</b> no campo Perícias da ficha dele.
                </p>
                {pagina.npcs.length === 0 ? (
                  <Vazio colecao="npcs" o="NPC" />
                ) : (
                  <div className="pe-cards">
                    {pagina.npcs.map((n) => (
                      <Link key={n.id} to={`/mundo/npcs/${n.id}`} className="pe-card pe-npc">
                        <span className="pe-inicial" style={{ '--c': corDe(pericia) } as React.CSSProperties}>
                          {n.nome.charAt(0)}
                        </span>
                        <span className="pe-card-txt">
                          <b>{n.nome}</b>
                          {n.local && <small>{ondeFica(n.local)}</small>}
                          {(n.lojaVenda || n.itensDaLoja.length > 0) && (
                            <small className="loja">
                              Vende{n.itensDaLoja.length ? `: ${nomes(n.itensDaLoja)}` : ''}
                            </small>
                          )}
                          {n.quests.length > 0 && <small>{n.quests.length} quest(s)</small>}
                        </span>
                      </Link>
                    ))}
                  </div>
                )}
              </section>

              {/* ---------------------------------------------------- quests */}
              <section className="gd-secao" id="quests">
                <h2>Quests</h2>
                <p className="gd-nota">
                  Uma quest pode envolver várias perícias, e aparece na página de cada uma.
                </p>
                {pagina.quests.length === 0 ? (
                  <Vazio colecao="quests" o="quest" />
                ) : (
                  <div className="gd-rolagem">
                    <table className="pe-tabela">
                      <thead>
                        <tr>
                          <th>Quest</th>
                          <th>Quem dá</th>
                          <th>Perícias envolvidas</th>
                          <th>Recompensa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagina.quests.map((q) => (
                          <tr key={q.id}>
                            <td>
                              <Link to={`/mundo/quests/${q.id}`}>{q.nome}</Link>
                              {q.tipo && <small>{q.tipo}</small>}
                            </td>
                            <td>{q.giver ? <Link to={`/mundo/npcs/${q.giver.id}`}>{q.giver.nome}</Link> : '—'}</td>
                            <td>
                              <span className="pe-chips">
                                {[id, ...q.pericias.filter((x) => x !== id)]
                                  .filter((x) => q.pericias.includes(x))
                                  .map((x) => {
                                    const p = periciaPorId(x);
                                    if (!p) return null;
                                    return (
                                      <button
                                        key={x}
                                        type="button"
                                        className={`pe-chip${x === id ? ' on' : ''}`}
                                        style={{ '--c': corDe(p) } as React.CSSProperties}
                                        onClick={() => abrir(x)}
                                      >
                                        <PericiaIcone pericia={x} cor={corDe(p)} tamanho={14} />
                                        {p.rotulo}
                                      </button>
                                    );
                                  })}
                              </span>
                            </td>
                            <td>
                              {q.itensDeRecompensa.length > 0 && <span>{nomes(q.itensDeRecompensa)}</span>}
                              {q.recompensas && <small>{q.recompensas}</small>}
                              {!q.recompensas && q.itensDeRecompensa.length === 0 && '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* --------------------------------------------------- eventos */}
              <section className="gd-secao" id="eventos">
                <h2>Eventos</h2>
                {pagina.eventos.length === 0 ? (
                  <Vazio colecao="eventos" o="evento" />
                ) : (
                  <div className="pe-cards">
                    {pagina.eventos.map((e) => (
                      <Link key={e.id} to={`/mundo/eventos/${e.id}`} className="pe-card pe-evento">
                        {e.tipo && <span className="pe-tipo">{e.tipo}</span>}
                        <b>{e.nome}</b>
                        {e.gatilho && (
                          <small>
                            <b>Quando:</b> {e.gatilho}
                          </small>
                        )}
                        {(e.areas.length > 0 || e.locais.length > 0) && (
                          <small>{nomes([...e.areas, ...e.locais])}</small>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </section>

              {/* ------------------------------------- recursos ou receitas */}
              {temMateria && (
                <section className="gd-secao" id="materia">
                  <h2>{natureza === 'coleta' ? 'Recursos e onde encontrar' : 'Receitas'}</h2>
                  <p className="gd-nota">
                    {natureza === 'coleta' ? (
                      <>
                        Os itens de categoria <b>coletável</b> e subcategoria{' '}
                        <b>{pericia.rotulo}</b>, e como se consegue cada um. Clique num
                        recurso para acender no mapa as áreas onde ele está.
                      </>
                    ) : (
                      <>
                        Os itens de categoria <b>receita</b> e subcategoria{' '}
                        <b>{pericia.rotulo}</b>. Uma receita pode juntar recursos de
                        várias coletas — a cor de cada ingrediente diz de onde ele vem.
                        Clique numa receita para acender no mapa onde está a matéria-prima.
                      </>
                    )}
                  </p>

                  <div className="pe-materia">
                    <div className="pe-materia-lista">
                      {itens.length === 0 && <Vazio colecao="itens" o="item" />}
                      {itens.map((i) => {
                        const Cartao = natureza === 'coleta' ? Recurso : Receita;
                        return (
                          <Cartao
                            key={i.item.id}
                            item={i}
                            cor={corDe(pericia)}
                            aceso={itemAceso === i.item.id}
                            aoClicar={() => setItemAceso(itemAceso === i.item.id ? null : i.item.id)}
                          />
                        );
                      })}
                    </div>

                    {season && (
                      <MapaDaPericia
                        season={season}
                        tetoNa={tetoNa}
                        acesas={aceso ? new Set(aceso.areas) : null}
                        rotulo={pericia.rotulo}
                        legenda={
                          natureza === 'coleta'
                            ? 'onde está o recurso escolhido'
                            : 'onde está a matéria-prima da receita escolhida'
                        }
                      />
                    )}
                  </div>
                </section>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}

const nomes = (lista: Resumo[]) => lista.map((r) => r.nome).join(', ');

function Vazio({ colecao, o }: { colecao: string; o: string }) {
  return (
    <p className="pe-vazio">
      Nenhum {o} ainda. Marque a perícia na ficha, em{' '}
      <Link to={`/mundo/${colecao}`}>Banco de Dados</Link>.
    </p>
  );
}

function SeloDeTier({ tier }: { tier?: string }) {
  const n = numeroDoTier(tier);
  return (
    <span className="gd-selo" style={{ '--c': n ? COR_TIER[n] : COR_INATIVA } as React.CSSProperties}>
      {n ? `T${n}` : 'T?'}
    </span>
  );
}

function Recurso({
  item,
  cor,
  aceso,
  aoClicar,
}: {
  item: ItemDaPericia;
  cor: string;
  aceso: boolean;
  aoClicar: () => void;
}) {
  const fontes = fontesDoItem(item.item);
  return (
    <div className={`pe-recurso${aceso ? ' on' : ''}`}>
      <button type="button" className="pe-recurso-botao" onClick={aoClicar} aria-pressed={aceso}>
        <SeloDeTier tier={item.item.tier} />
        <span className="pe-recurso-txt">
          <b>
            {item.item.nome}
            {item.item.raridade && <small> · {item.item.raridade}</small>}
          </b>
          {fontes.length === 0 ? (
            <small className="pe-sem-fonte">Ainda sem onde encontrar.</small>
          ) : (
            fontes.map((f) => {
              const estilo = f.tipo === 'coleta' ? cor : COR_DA_FONTE[f.tipo];
              return (
                <span key={f.tipo} className="pe-fonte">
                  <b style={{ color: estilo }}>{f.rotulo}</b> {f.nomes.join(', ')}
                </span>
              );
            })
          )}
          {item.item.usadoEm.length > 0 && <small>Usado em: {nomes(item.item.usadoEm)}</small>}
        </span>
      </button>
      <Link className="pe-abrir" to={`/mundo/itens/${item.item.id}`}>
        Abrir ficha
      </Link>
    </div>
  );
}

/**
 * Uma receita. A perícia de cada ingrediente já está na linha dele; o rodapé responde a
 * outra pergunta — onde se acha o item pronto, para quem não quer criá-lo.
 */
function Receita({
  item,
  cor,
  aceso,
  aoClicar,
}: {
  item: ItemDaPericia;
  cor: string;
  aceso: boolean;
  aoClicar: () => void;
}) {
  const fontes = fontesDoItem(item.item);
  return (
    <div className={`pe-card pe-receita${aceso ? ' on' : ''}`}>
      <button type="button" className="pe-receita-botao" onClick={aoClicar} aria-pressed={aceso}>
        <span className="pe-receita-topo">
          <SeloDeTier tier={item.item.tier} />
          <b>{item.item.nome}</b>
        </span>
        {item.receita.length === 0 ? (
          <small className="pe-sem-fonte">Receita ainda sem ingredientes.</small>
        ) : (
          <ul>
            {item.receita.map((r) => {
              const p = r.origem ? periciaPorId(r.origem) : undefined;
              return (
                <li key={r.item.id}>
                  <b>{r.quantidade}×</b>
                  {p ? <PericiaIcone pericia={p.id} cor={corDe(p)} tamanho={15} /> : <i className="pe-ponto" />}
                  <span>{r.item.nome}</span>
                  <small style={{ color: p ? corDe(p) : undefined }}>{p ? p.rotulo : 'outro'}</small>
                </li>
              );
            })}
          </ul>
        )}
        <span className="pe-encontrado">
          <span className="pe-encontrado-rotulo">Encontrado</span>
          {fontes.length === 0 ? (
            <small className="pe-sem-fonte">Só criando.</small>
          ) : (
            fontes.map((f) => (
              <span key={f.tipo} className="pe-fonte">
                <b style={{ color: f.tipo === 'coleta' ? cor : COR_DA_FONTE[f.tipo] }}>{f.rotulo}</b>{' '}
                {f.nomes.join(', ')}
              </span>
            ))
          )}
        </span>
      </button>
      <Link className="pe-abrir" to={`/mundo/itens/${item.item.id}`}>
        Abrir ficha
      </Link>
    </div>
  );
}

/**
 * O mapa da Season com o teto da perícia em cada área.
 *
 * <p>Mais simples que o da Tier List, de propósito: lá o mapa é onde se escolhe; aqui é
 * onde se lê. O tier da área sai, o teto da perícia entra, e a borda dourada mostra
 * onde está o recurso aceso na lista.
 */
function MapaDaPericia({
  season,
  tetoNa,
  acesas,
  rotulo,
  legenda,
}: {
  season: NonNullable<ReturnType<typeof mapaDaSeason>>;
  tetoNa: (a: AreaComTier) => number | undefined;
  acesas: Set<number> | null;
  rotulo: string;
  /** O que a borda dourada quer dizer — muda entre recurso e receita. */
  legenda: string;
}) {
  if (!season.areas.length) return null;
  const xs = season.areas.map((a) => a.x);
  const ys = season.areas.map((a) => a.y);
  const folga = 40;
  const minX = Math.min(...xs) - 130 - folga;
  const minY = Math.min(...ys) - 150 - folga;
  const caixa = `${minX} ${minY} ${Math.max(...xs) + 130 + folga - minX} ${Math.max(...ys) + 150 + folga - minY}`;

  return (
    <figure className="pe-mapa">
      <svg viewBox={caixa} role="img" aria-label={`Até que tier ${rotulo} vai em cada área`}>
        {season.areas.map((a) => {
          const teto = tetoNa(a);
          const acesa = acesas && a.areaId != null && acesas.has(a.areaId);
          return (
            <g key={`${a.regiaoSlot}-${a.slot}`}>
              <title>{`${semTier(a.nome)} · ${a.regiao} · ${rotulo} até T${teto ?? TIER_DE_TODA_AREA}`}</title>
              <polygon
                points={pontosDoHexagono(a.x, a.y)}
                fill={a.cor}
                fillOpacity={teto ? 0.62 : 0.2}
                stroke={acesa ? '#e6c787' : '#0b0f0c'}
                strokeWidth={acesa ? 14 : 6}
                strokeLinejoin="round"
              />
              <text x={a.x} y={a.y - 4} textAnchor="middle" className="pe-mapa-tier" fill={teto ? COR_TIER[teto] : '#8e8875'}>
                T{teto ?? TIER_DE_TODA_AREA}
              </text>
              <text x={a.x} y={a.y + 40} textAnchor="middle" className="pe-mapa-nome">
                {semTier(a.nome).length > 16 ? `${semTier(a.nome).slice(0, 15)}…` : semTier(a.nome)}
              </text>
            </g>
          );
        })}
      </svg>
      <figcaption>
        Até que tier {rotulo} vai em cada área — o mesmo dado da{' '}
        <Link to="/game-design/areas-e-tier">Tier List</Link>. Borda dourada: {legenda}.
      </figcaption>
    </figure>
  );
}
